import type { ApiError, AuthResponse } from '@agenda-facil/contracts';
import { useAuthStore } from '../stores/auth-store';

const API_URL =
  import.meta.env.VITE_API_URL ??
  (globalThis.location?.hostname.endsWith('painelmv.com.br')
    ? 'https://api.agenda.painelmv.com.br/api'
    : 'http://localhost:3001/api');

export class ApiClientError extends Error {
  constructor(public readonly details: ApiError) {
    super(details.message);
  }
}

interface RequestOptions extends RequestInit {
  retryAuth?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { retryAuth = true, ...init } = options;
  const token = useAuthStore.getState().accessToken;
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json');
  if (token) headers.set('authorization', `Bearer ${token}`);
  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    headers,
    credentials: 'include',
    signal: init.signal ?? AbortSignal.timeout(8_000),
  });
  if (response.status === 401 && retryAuth && path !== '/auth/refresh') {
    const refreshed = await refreshSession();
    if (refreshed) return request<T>(path, { ...options, retryAuth: false });
  }
  if (!response.ok) {
    if (response.status === 401) {
      useAuthStore.getState().clear();
    }
    const raw = (await response.json().catch(() => undefined)) as unknown;
    const details: ApiError = isApiError(raw)
      ? raw
      : { statusCode: response.status, code: 'REQUEST_FAILED', message: 'Não foi possível concluir.' };
    throw new ApiClientError(details);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === 'object' &&
    value !== null &&
    'statusCode' in value &&
    'code' in value &&
    'message' in value
  );
}

export async function refreshSession(): Promise<boolean> {
  try {
    const response = await request<AuthResponse | undefined>('/auth/refresh', { method: 'POST', retryAuth: false });
    if (!response) {
      useAuthStore.getState().clear();
      return false;
    }
    useAuthStore.getState().setSession(response.accessToken, response.user);
    return true;
  } catch {
    useAuthStore.getState().clear();
    return false;
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, value?: unknown) =>
    request<T>(path, { method: 'POST', ...(value === undefined ? {} : { body: JSON.stringify(value) }) }),
  put: <T>(path: string, value: unknown) => request<T>(path, { method: 'PUT', body: JSON.stringify(value) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
